// @ts-ignore
import { initAndGetAnalyticsSdk } from "@mmt/event-logger";
import {
  getEventDetails,
  getEventTrackingContext,
  getVendorErrorDetails,
} from "./pdtutils";
import { generateRequestId } from "..";
import { getPartner, LOB } from "../../constants";
import { getCommonPdtDataV2 } from "./getCommonPdtDataV2";
import PdtSchemaUpdater from "./PdtSchemaUpdater";
import {
  IEventDetailInput,
  IEventTrackingContext,
  IExperimentDetails,
  IPageContextInput,
} from "./types";

export interface IPdtDataV2 {
  event_detail: IEventDetailInput;
  page_context?: IPageContextInput;
  event_tracking_context?: IEventTrackingContext;
  user_id?: string | null;
  partner_id?: string | null;
  funnelType?: string | null;
  category?: string | null;
  experiment_details?: IExperimentDetails;
  shouldFlushEvents?: boolean;
}

const AnalyticsSdk = initAndGetAnalyticsSdk({});

export const getPdtLoggerMethods = ({
  lobFunnelType,
  userId,
  puid,
  lobCategory,
  pdtEvents,
  funnelStep,
}: {
  lobFunnelType: string;
  userId?: string | null;
  puid?: string | null;
  lobCategory?: string | null;
  pdtEvents: Record<string, string>;
  funnelStep?: string;
}) => {
  const partner = getPartner();

  const sendCTAData = ({
    pdtData = {},
    eventDetail,
    event_tracking_context,
  }: {
    eventDetail: IEventDetailInput;
    pdtData?: any;
    event_tracking_context?: IEventTrackingContext;
  }) => {
    try {
      pdtData = getCommonPdtDataV2({
        partner,
        userId,
        puid,
        lobFunnelType,
        lobCategory,
        funnelStep,
      });
      AnalyticsSdk.setRuntimeContextProvider({
        getUserContext: () => {
          return pdtData.user_context;
        },
        getDeviceContext: () => {
          return pdtData.device_context;
        },
      });
      const pdtSchemaUpdater = new PdtSchemaUpdater(pdtData);

      const data = pdtSchemaUpdater
        .mergeEventDetailsData(eventDetail)
        .mergeEventTrackingContext({
          ...event_tracking_context,
          request_id: event_tracking_context?.request_id ?? generateRequestId(),
        } as IEventTrackingContext)
        .getSchema();
      updateEventName(data.event_detail, pdtEvents);
      AnalyticsSdk.track(data, LOB.FOREX);
    } catch (e: any) {
      console.error(e?.message);
    }
  };

  const sendPdtDataV2 = ({
    event_detail,
    page_context,
    event_tracking_context,
    user_id,
    partner_id,
    funnelType,
    category,
    experiment_details,
    shouldFlushEvents = false,
  }: IPdtDataV2) => {
    try {
      const commonPdtData = getCommonPdtDataV2({
        partner,
        userId: user_id ?? userId,
        puid: partner_id ?? puid,
        lobFunnelType: funnelType ?? lobFunnelType,
        lobCategory: category ?? lobCategory,
        funnelStep,
      });
      AnalyticsSdk.setRuntimeContextProvider({
        getUserContext: () => {
          return commonPdtData.user_context;
        },
        getDeviceContext: () => {
          return commonPdtData.device_context;
        },
      });
      const pdtSchemaUpdater = new PdtSchemaUpdater(commonPdtData);
      const data = pdtSchemaUpdater
        .mergeEventDetailsData(event_detail)
        .mergePageContextData(page_context)
        .mergeEventTrackingContext({
          ...event_tracking_context,
          request_id: event_tracking_context?.request_id ?? generateRequestId(),
        } as IEventTrackingContext)
        .mergeExperimentDetails(experiment_details)
        .getSchema();
      updateEventName(data.event_detail, pdtEvents);

      AnalyticsSdk.track(data, LOB.FOREX);
      if (shouldFlushEvents) {
        AnalyticsSdk?.flushEvents();
      }
    } catch (e) {
      console.error("sendPdtDataV2: ", e);
    }
  };

  const getPdtDataV2 = ({
    event_detail,
    page_context,
    event_tracking_context,
    user_id,
    partner_id,
    funnelType,
    category,
    experiment_details,
  }: IPdtDataV2) => {
    try {
      const commonPdtData = getCommonPdtDataV2({
        partner,
        userId: user_id ?? userId,
        puid: partner_id ?? puid,
        lobFunnelType: funnelType ?? lobFunnelType,
        lobCategory: category ?? lobCategory,
        funnelStep,
      });

      const pdtSchemaUpdater = new PdtSchemaUpdater(commonPdtData);
      const data = pdtSchemaUpdater
        .mergeEventDetailsData(event_detail)
        .mergePageContextData(page_context)
        .mergeEventTrackingContext({
          ...event_tracking_context,
          request_id: event_tracking_context?.request_id ?? generateRequestId(),
        } as IEventTrackingContext)
        .mergeExperimentDetails(experiment_details)
        .getSchema();
      updateEventName(data.event_detail, pdtEvents);

      return data;
    } catch (e) {
      console.error("getPdtDataV2: ", e);
      return null;
    }
  };

  return {
    sendCTAData,
    sendPdtDataV2,
    getPdtDataV2,
  };
};

export function sendPdtErrorEvent({
  appErrorReducer,
  sendPdtDataV2,
}: {
  appErrorReducer: any;
  sendPdtDataV2: (data: IPdtDataV2) => void;
}) {
  try {
    const { apiUrl } = appErrorReducer || {};
    const errorMessage =
      appErrorReducer?.pdtInfo?.errorDescription ||
      appErrorReducer?.errorTitle ||
      appErrorReducer?.errorDescription ||
      appErrorReducer?.message;

    if (!errorMessage) {
      return;
    }

    sendPdtDataV2({
      event_detail: getEventDetails({
        event_name: "NA",
        vendor_error_details: getVendorErrorDetails({
          error_code:
            appErrorReducer?.pdtInfo?.code ||
            appErrorReducer?.code ||
            "NO_CODE_FOUND",
          error_message_shown: errorMessage,
          api_url: apiUrl,
        }),
      }),
      event_tracking_context: getEventTrackingContext({
        request_id: appErrorReducer?.requestId || "",
      }),
    });
  } catch (e) {
    console.error("sendPdtErrorEvent fn ", e);
  }
}

function updateEventName(
  eventDetail: IEventDetailInput,
  pdtEvents: Record<string, string>
) {
  const eventName = eventDetail?.event_name || "";
  if (eventName) {
    eventDetail.event_name = pdtEvents[eventName] || eventName;
  }
}

export const replacePipeWithComma = (data: any) => {
  data?.event_detail?.components?.content_details?.forEach((item: any) => {
    if (typeof item.description === "string") {
      item.description = item.description.replace(/\|/g, ",");
    }
  });
  return data;
};

export const transformComponentData = (components?: any) => {
  const contentDetails = components?.content_details;
  if (!contentDetails || contentDetails.length === 0) {
    return "";
  }

  const transformedItems = contentDetails
    .filter((content: any) => content != null)
    .map((content: any) => {
      const values = [];
      if (content.id && content.id !== "") values.push(`${content.id}`);
      if (content.type && content.type !== "") values.push(`${content.type}`);
      if (content.description && content.description !== "")
        values.push(`${content.description}`);
      return values.join(":");
    });

  return transformedItems.join("|");
};

export const transformUpcomingTrip = (upcomingTrip?: any) => {
  const tripDetails = upcomingTrip?.trip_details;
  if (!tripDetails || tripDetails.length === 0) {
    return "";
  }

  // Transform only the first trip detail
  const firstTrip = tripDetails[0];
  if (!firstTrip) {
    return "";
  }

  const values = [];
  if (firstTrip.intent && firstTrip.intent !== "")
    values.push(`${firstTrip.intent}`);
  if (firstTrip.dependent_lob && firstTrip.dependent_lob !== "")
    values.push(`${firstTrip.dependent_lob}`);
  if (firstTrip.country_code && firstTrip.country_code !== "")
    values.push(`${firstTrip.country_code}`);
  if (firstTrip.rtt) values.push(`${firstTrip.rtt}`);
  if (firstTrip.opportunity_type && firstTrip.opportunity_type !== "")
    values.push(`${firstTrip.opportunity_type}`);

  return values.join(":");
};
